import { useState, type FormEvent } from 'react';
import { useAuth, type TwoFactorSetupData } from '../contexts/AuthContext';
import { ApiError } from '../lib/apiClient';
import { LogoMark } from './LogoMark';

/**
 * Forced 2FA enrollment. When the backend's admin enrollment gate is on, an admin-capable
 * account without 2FA is refused (403 TWO_FACTOR_ENROLLMENT_REQUIRED) everywhere except the
 * 2FA endpoints; this screen walks the user through `POST /auth/2fa/setup` (QR + secret) and
 * `POST /auth/2fa/verify` (first code enables 2FA and returns the one-time backup codes).
 * The portal is English-only today, so every string lives in TWO_FACTOR_ENROLLMENT_TEXT.
 * The QR, the secret and the backup codes live in component state only: never persisted,
 * never logged, gone on unmount. Sign out stays available on every step.
 */
const TWO_FACTOR_ENROLLMENT_TEXT = {
  heroTitle: 'Secure your account to open the console.',
  heroBody: 'Admin accounts must use a second factor.',
  signOut: 'Sign out',

  introTitle: 'Set up two-factor authentication',
  introBody:
    'Admin accounts must be protected with a second factor. You will link an authenticator app to your account, then confirm it with a 6-digit code.',
  start: 'Start',
  starting: 'Starting…',
  setupFailed: 'We could not start the setup. Check your connection and try again.',
  setupAlreadyEnabled: 'Two-factor authentication is already enabled for this account. Sign out and sign in again.',

  scanTitle: 'Link your authenticator app',
  scanStep1: 'Open an authenticator app such as Google Authenticator, Microsoft Authenticator or Authy.',
  scanStep2: 'Scan this QR code. If you cannot scan it, enter the secret below by hand.',
  scanStep3: 'Enter the 6-digit code the app shows.',
  qrAlt: 'QR code to add your MedicLynk admin account to an authenticator app',
  secretLabel: 'Manual entry secret',
  copySecret: 'Copy secret',
  copied: 'Copied',
  copyFailed: 'Copy failed. Select the text and copy it by hand.',
  codeLabel: 'Authentication code',
  verify: 'Verify and enable',
  verifying: 'Verifying…',

  invalidFormat: 'Enter the 6-digit code from your authenticator app.',
  invalidCode: 'That code is not valid. Check the code and try again.',
  tooManyRequests: 'Too many attempts. Wait a minute, then try again.',
  locked: 'Too many failed attempts. This account is temporarily locked; try again later.',
  generic: 'We could not verify the code. Check your connection and try again.',

  backupTitle: 'Save your backup codes',
  backupBody:
    'Each backup code works once if you lose access to your authenticator app. They are shown only now: store them somewhere safe.',
  backupNone: 'Two-factor authentication is enabled. The server did not return backup codes.',
  copyCodes: 'Copy codes',
  downloadCodes: 'Download (.txt)',
  downloadHeader: 'MedicLynk admin portal - two-factor backup codes',
  downloadNote: 'Each code works once. Keep this file somewhere safe.',
  downloadFileName: 'mediclynk-admin-backup-codes.txt',
  acknowledge: 'I saved these codes',
  continue: 'Continue',
  backupActionFailed: 'That did not work. Select the codes and copy them by hand.',
} as const;

type Step = 'intro' | 'scan' | 'backup';

/** Strip whitespace pasted along with the code. */
const normalizeCode = (raw: string): string => raw.replace(/\s/g, '');

/** Group the base32 secret in blocks of four so it is easier to read and type. */
const groupSecret = (secret: string): string => secret.replace(/(.{4})/g, '$1 ').trim();

const describeSetupError = (err: unknown): string => {
  if (err instanceof ApiError) {
    if (err.statusCode === 429) return TWO_FACTOR_ENROLLMENT_TEXT.tooManyRequests;
    if (err.statusCode === 400) return TWO_FACTOR_ENROLLMENT_TEXT.setupAlreadyEnabled;
  }
  return TWO_FACTOR_ENROLLMENT_TEXT.setupFailed;
};

const describeVerifyError = (err: unknown): string => {
  if (err instanceof ApiError) {
    if (err.statusCode === 429) return TWO_FACTOR_ENROLLMENT_TEXT.tooManyRequests;
    if (err.statusCode === 400) {
      // The backend reports lockout as a 400 whose message mentions the lock.
      return /locked|too many/i.test(err.message)
        ? TWO_FACTOR_ENROLLMENT_TEXT.locked
        : TWO_FACTOR_ENROLLMENT_TEXT.invalidCode;
    }
  }
  return TWO_FACTOR_ENROLLMENT_TEXT.generic;
};

export function TwoFactorEnrollment() {
  const { startTwoFactorSetup, completeTwoFactorEnrollment, finishTwoFactorEnrollment, signOut } = useAuth();
  const [step, setStep] = useState<Step>('intro');
  const [setup, setSetup] = useState<TwoFactorSetupData | null>(null);
  const [backupCodes, setBackupCodes] = useState<string[]>([]);
  const [code, setCode] = useState('');
  const [acknowledged, setAcknowledged] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const handleStart = async () => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      setSetup(await startTwoFactorSetup());
      setStep('scan');
    } catch (err) {
      setError(describeSetupError(err));
    } finally {
      setBusy(false);
    }
  };

  const handleVerify = async (event: FormEvent) => {
    event.preventDefault();
    if (busy) return;
    const trimmed = normalizeCode(code);
    if (!/^\d{6}$/.test(trimmed)) {
      setError(TWO_FACTOR_ENROLLMENT_TEXT.invalidFormat);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const codes = await completeTwoFactorEnrollment(trimmed);
      // 2FA is on from here: the QR and secret are no longer needed.
      setSetup(null);
      setCode('');
      setBackupCodes(codes);
      setStep('backup');
    } catch (err) {
      setError(describeVerifyError(err));
      setCode('');
    } finally {
      setBusy(false);
    }
  };

  const copyText = async (text: string, okMessage: string, failMessage: string) => {
    setNotice(null);
    try {
      await navigator.clipboard.writeText(text);
      setNotice(okMessage);
    } catch {
      setNotice(failMessage);
    }
  };

  const handleDownload = () => {
    setNotice(null);
    try {
      const body = [
        TWO_FACTOR_ENROLLMENT_TEXT.downloadHeader,
        TWO_FACTOR_ENROLLMENT_TEXT.downloadNote,
        '',
        ...backupCodes,
        '',
      ].join('\n');
      const url = URL.createObjectURL(new Blob([body], { type: 'text/plain' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = TWO_FACTOR_ENROLLMENT_TEXT.downloadFileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch {
      setNotice(TWO_FACTOR_ENROLLMENT_TEXT.backupActionFailed);
    }
  };

  const handleContinue = () => {
    // Drop the codes from memory before the app resumes.
    setBackupCodes([]);
    finishTwoFactorEnrollment();
  };

  const canContinue = backupCodes.length === 0 || acknowledged;

  return (
    <div className="login-screen">
      <div className="login-hero">
        <div className="login-hero-mark" aria-hidden="true">
          <LogoMark size={480} />
        </div>
        <div className="login-hero-content">
          <LogoMark size={40} />
          <h1>{TWO_FACTOR_ENROLLMENT_TEXT.heroTitle}</h1>
          <p>{TWO_FACTOR_ENROLLMENT_TEXT.heroBody}</p>
        </div>
        <div className="login-hero-foot">© {new Date().getFullYear()} MedicLynk. Internal use only.</div>
      </div>

      <div className="login-panel">
        <div className="login-panel-inner">
          <LogoMark size={36} />

          {step === 'intro' && (
            <>
              <h2>{TWO_FACTOR_ENROLLMENT_TEXT.introTitle}</h2>
              <p className="auth-subtitle">{TWO_FACTOR_ENROLLMENT_TEXT.introBody}</p>
              {error && (
                <p className="auth-error" role="alert">
                  {error}
                </p>
              )}
              <button type="button" className="btn btn-primary tfa-submit" onClick={() => void handleStart()} disabled={busy}>
                {busy ? TWO_FACTOR_ENROLLMENT_TEXT.starting : TWO_FACTOR_ENROLLMENT_TEXT.start}
              </button>
            </>
          )}

          {step === 'scan' && setup && (
            <form onSubmit={handleVerify} noValidate className="tfa-form">
              <h2>{TWO_FACTOR_ENROLLMENT_TEXT.scanTitle}</h2>
              <ol className="tfa-steps">
                <li>{TWO_FACTOR_ENROLLMENT_TEXT.scanStep1}</li>
                <li>{TWO_FACTOR_ENROLLMENT_TEXT.scanStep2}</li>
                <li>{TWO_FACTOR_ENROLLMENT_TEXT.scanStep3}</li>
              </ol>

              <img className="tfa-qr" src={setup.qrCodeDataUrl} alt={TWO_FACTOR_ENROLLMENT_TEXT.qrAlt} width={200} height={200} />

              <span className="tfa-label" id="tfa-secret-label">
                {TWO_FACTOR_ENROLLMENT_TEXT.secretLabel}
              </span>
              <div className="tfa-secret-row">
                <code className="tfa-secret" aria-labelledby="tfa-secret-label">
                  {groupSecret(setup.secret)}
                </code>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() =>
                    void copyText(
                      setup.secret,
                      TWO_FACTOR_ENROLLMENT_TEXT.copied,
                      TWO_FACTOR_ENROLLMENT_TEXT.copyFailed,
                    )
                  }
                >
                  {TWO_FACTOR_ENROLLMENT_TEXT.copySecret}
                </button>
              </div>
              {notice && (
                <p className="tfa-notice" role="status">
                  {notice}
                </p>
              )}

              {error && (
                <p className="auth-error" role="alert">
                  {error}
                </p>
              )}

              <label htmlFor="two-factor-enroll-code" className="tfa-label">
                {TWO_FACTOR_ENROLLMENT_TEXT.codeLabel}
              </label>
              <input
                id="two-factor-enroll-code"
                className="form-control tfa-code-input"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                autoCorrect="off"
                spellCheck={false}
                maxLength={12}
                value={code}
                onChange={(e) => setCode(e.target.value)}
                disabled={busy}
                aria-invalid={error ? true : undefined}
              />

              <button type="submit" className="btn btn-primary tfa-submit" disabled={busy}>
                {busy ? TWO_FACTOR_ENROLLMENT_TEXT.verifying : TWO_FACTOR_ENROLLMENT_TEXT.verify}
              </button>
            </form>
          )}

          {step === 'backup' && (
            <>
              <h2>{TWO_FACTOR_ENROLLMENT_TEXT.backupTitle}</h2>
              {backupCodes.length > 0 ? (
                <>
                  <p className="auth-subtitle">{TWO_FACTOR_ENROLLMENT_TEXT.backupBody}</p>
                  <ul className="tfa-codes" aria-label={TWO_FACTOR_ENROLLMENT_TEXT.backupTitle}>
                    {backupCodes.map((backupCode) => (
                      <li key={backupCode}>
                        <code>{backupCode}</code>
                      </li>
                    ))}
                  </ul>
                  <div className="tfa-actions">
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() =>
                        void copyText(
                          backupCodes.join('\n'),
                          TWO_FACTOR_ENROLLMENT_TEXT.copied,
                          TWO_FACTOR_ENROLLMENT_TEXT.backupActionFailed,
                        )
                      }
                    >
                      {TWO_FACTOR_ENROLLMENT_TEXT.copyCodes}
                    </button>
                    <button type="button" className="btn btn-secondary" onClick={handleDownload}>
                      {TWO_FACTOR_ENROLLMENT_TEXT.downloadCodes}
                    </button>
                  </div>
                  {notice && (
                    <p className="tfa-notice" role="status">
                      {notice}
                    </p>
                  )}
                  <label className="tfa-check">
                    <input type="checkbox" checked={acknowledged} onChange={(e) => setAcknowledged(e.target.checked)} />
                    <span>{TWO_FACTOR_ENROLLMENT_TEXT.acknowledge}</span>
                  </label>
                </>
              ) : (
                <p className="auth-subtitle">{TWO_FACTOR_ENROLLMENT_TEXT.backupNone}</p>
              )}
              <button type="button" className="btn btn-primary tfa-submit" onClick={handleContinue} disabled={!canContinue}>
                {TWO_FACTOR_ENROLLMENT_TEXT.continue}
              </button>
            </>
          )}

          <div className="tfa-links">
            <button type="button" className="tfa-link" onClick={() => void signOut()}>
              {TWO_FACTOR_ENROLLMENT_TEXT.signOut}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
